import React, { forwardRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    label, 
    error, 
    helperText, 
    leftIcon, 
    rightIcon, 
    fullWidth = false, 
    className = '', 
    ...props 
  }, ref) => {
    // Glassmorphic input styles
    const baseInputClasses = 'bg-gray-800/50 backdrop-blur-md border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-300 shadow-inner';
    const errorClasses = error ? 'border-red-500/70 focus:ring-red-500 focus:border-red-400' : 'border-gray-600/50 focus:ring-primary-500 focus:border-primary-400/50 hover:border-gray-500/70';
    const widthClass = fullWidth ? 'w-full' : '';
    const paddingLeftClass = leftIcon ? 'pl-10' : '';
    const paddingRightClass = rightIcon ? 'pr-10' : '';
    
    return (
      <div className={`${widthClass}`}>
        {label && (
          <label className="block text-sm font-medium text-gray-300 mb-1 transition-colors duration-300 hover:text-primary-400">
            {label}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
              {leftIcon}
            </div>
          )}
          
          <input
            ref={ref}
            className={`${baseInputClasses} ${errorClasses} ${paddingLeftClass} ${paddingRightClass} ${widthClass} ${className}`}
            {...props}
          />
          
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>
        
        {(error || helperText) && (
          <p className={`mt-1 text-sm ${error ? 'text-red-500' : 'text-gray-400'}`}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
